import { useState } from "react";
import {
  Phone,
  ArrowLeft,
  ArrowRight,
  Loader2,
  AlertCircle,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
const API_URL = process.env.REACT_APP_BACKEND_URL;
if (!API_URL) {
  console.error("REACT_APP_BACKEND_URL is missing in frontend/.env");
}

function ForgetPassword() {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [errors, setErrors] = useState({ mobile: "", form: "" });

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setErrors({ mobile: "Enter a valid 10-digit mobile number", form: "" });
      return;
    }
    setErrors({ mobile: "", form: "" });

    try {
  setLoading(true);

  const res = await axios.post(
    `${API_URL}/api/auth/forgot-password`,
    {
      mobile,
    }
  );

  if (res.data.success) {

    localStorage.setItem("resetUserId", res.data.userId);

    if (res.data.email) {
      localStorage.setItem("email", res.data.email);
    }

    navigate("/otp", {
      state: {
        type: "forgot-password",
        mobile,
      },
    });

  } else {

    setErrors({
      mobile: "",
      form: res.data.message || "Something went wrong",
    });

  }

} catch (err) {

  setErrors({
    mobile: "",
    form:
      err.response?.data?.message ||
      "Unable to connect. Please try again.",
  });

} finally {

  setLoading(false);

}
  };

  return (
    <div className="min-h-[100dvh] w-full bg-slate-50 relative overflow-hidden flex items-center justify-center px-5 py-10">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
        .anim-fade-up{animation:fadeUp .6s cubic-bezier(.22,1,.36,1) both;}
        .delay-1{animation-delay:.05s;} .delay-2{animation-delay:.15s;}
        .delay-3{animation-delay:.25s;} .delay-4{animation-delay:.35s;}
        .delay-5{animation-delay:.45s;} .delay-6{animation-delay:.55s;}
      `}</style>

      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-sky-100/70 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="anim-fade-up delay-2 rounded-2xl bg-white border border-slate-200/70 shadow-xl shadow-slate-200/60 p-7 sm:p-9">
          {/* Icon badge */}
          <div className="anim-fade-up delay-3 mx-auto mb-5 h-14 w-14 rounded-xl bg-blue-50 border border-blue-100 grid place-items-center">
            <KeyRound size={22} className="text-blue-600" />
          </div>

          <div className="text-center">
            <h2 className="anim-fade-up delay-3 text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900">
              Forgot password?
            </h2>
            <p className="anim-fade-up delay-4 mt-2 text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
              Enter your registered mobile number and we'll send an OTP to your email.
            </p>
          </div>

          {errors.form && (
            <div
              role="alert"
              className="mt-6 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{errors.form}</span>
            </div>
          )}

          <form onSubmit={handleContinue} noValidate className="mt-7 space-y-5">
            <div className="anim-fade-up delay-5">
              <label
                htmlFor="mobile"
                className="text-xs font-medium text-slate-700 mb-1.5 block"
              >
                Mobile Number
              </label>
              <div
                className={`flex items-center rounded-lg border bg-white transition
                  ${
                    errors.mobile
                      ? "border-red-400 ring-2 ring-red-100"
                      : focused
                      ? "border-blue-500 ring-4 ring-blue-100"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
              >
                <span className="pl-3.5 text-slate-400">
                  <Phone size={18} />
                </span>
                <span className="pl-2 pr-2 text-sm text-slate-500 border-r border-slate-200 py-3 select-none">
                  +91
                </span>
                <input
                  id="mobile"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={mobile}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter your number"
                  className="w-full min-w-0 bg-transparent px-3 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none"
                  autoComplete="tel"
                  aria-invalid={!!errors.mobile}
                />
              </div>
              {errors.mobile && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.mobile}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="anim-fade-up delay-6 group w-full rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 py-3.5 font-medium text-white shadow-md shadow-blue-500/20 transition-all
                        active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px]
                        flex items-center justify-center gap-2 text-[15px]"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending OTP…
                </>
              ) : (
                <>
                  Send OTP
                  <ArrowRight
                    size={18}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </>
              )}
            </button>

            <Link
              to="/"
              className="group flex items-center justify-center gap-1.5 text-sm text-slate-600 hover:text-blue-600 transition"
            >
              <ArrowLeft
                size={16}
                className="transition-transform group-hover:-translate-x-0.5"
              />
              Back to Login
            </Link>

            <div className="flex items-center justify-center gap-1.5 pt-1 text-[11px] text-slate-500">
              <ShieldCheck size={13} className="text-emerald-500" />
              Secured with 256-bit encryption
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400 tracking-wide">
          © 2026 ZA Optical
        </p>
      </div>
    </div>
  );
}

export default ForgetPassword;